import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { UrlShortener } from './UrlShortener';
import { UrlList } from './UrlList';
import { ShortenedUrlResponse } from '../../utils/sqrone-api';
import { useAuth } from '../../contexts/AuthContext';
import {
  Home as HomeIcon,
  LayoutGrid,
  Monitor,
  FileText,
  Settings,
  Clock,
  Lock,
  AlertCircle,
  WifiOff,
  Star,
  Activity,
  Shield,
  RefreshCw,
  QrCode,
  Globe,
  XCircle,
  CheckCircle2
} from 'lucide-react';

import './Home.css';

// Re-export for backward compatibility
export type { ShortenedUrlResponse };

function Home() {
  const { isAuthenticated, login } = useAuth();
  const [urls, setUrls] = useState<ShortenedUrlResponse[]>([]);
  const [showClaimedMessage, setShowClaimedMessage] = useState(false);

  // Load from local storage on mount and when auth state changes
  useEffect(() => {
    const stored = localStorage.getItem('shortened-urls');
    if (stored) {
      setUrls(JSON.parse(stored));
      setShowClaimedMessage(false);
    } else {
      if (urls.length > 0 && isAuthenticated) {
        setShowClaimedMessage(true);
      }
      setUrls([]);
    }
  }, [isAuthenticated]);

  // FS11: Redirect authenticated users to All Checkpoints
  if (isAuthenticated) {
    return <Navigate to="/manage" replace />;
  }

  const saveToLocalStorage = (updatedUrls: ShortenedUrlResponse[]) => {
    setUrls(updatedUrls);
    localStorage.setItem('shortened-urls', JSON.stringify(updatedUrls));
  };

  const handleUrlShortened = (url: ShortenedUrlResponse) => {
    const updatedUrls = [url, ...urls];
    saveToLocalStorage(updatedUrls);
  };

  const handleDelete = (shortCode: string) => {
    const updatedUrls = urls.filter(url => url.shortCode !== shortCode);
    saveToLocalStorage(updatedUrls);
  };

  const handleUpdate = (shortCode: string, updates: Partial<ShortenedUrlResponse>) => {
    const updatedUrls = urls.map(url =>
      url.shortCode === shortCode ? { ...url, ...updates } : url
    );
    saveToLocalStorage(updatedUrls);
  };

  return (
    <div className="landing">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-glow"></div>
        <div className="container">
          <div className="hero-badge">
            <span className="hero-badge-dot"></span>
            Operations-first infrastructure
          </div>
          <h1 className="hero-title">
            Control how people<br/>interact with <span className="text-gradient">real</span><br/>and <span className="text-gradient">digital</span> spaces
          </h1>
          <p className="hero-subtitle">
            Confcall lets you define and operate Checkpoints for events, venues, campuses, or internal tools — reachable via QR, NFC, or links, even offline, and fully auditable.
          </p>

          <div className="try-now-card">
            <div className="try-now-label">Try it now — no account needed</div>
            <UrlShortener onUrlShortened={handleUrlShortened} />
            <p className="try-now-subtext">No account required — claim your Checkpoint later.</p>

            {urls.length > 0 && (
              <div className="try-now-results">
                <UrlList urls={urls} onDelete={handleDelete} onUpdate={handleUpdate} />
              </div>
            )}
            {showClaimedMessage && (
              <div className="claimed-message">
                <p className="claimed-title">Your checkpoints have been claimed!</p>
                <p className="claimed-link">
                  View them in <a href="/manage">All Checkpoints</a>
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* What is a Checkpoint Section */}
      <section className="checkpoint-section" id="checkpoints">
        <div className="container">
          <div className="section-label">Understanding Checkpoints</div>
          <h2 className="section-title">What is a Checkpoint?</h2>
          <p className="section-description">
            A Checkpoint represents a real or logical place where people interact with something — physical or digital. You don't manage QR codes, NFC, or links. You manage what happens at the Checkpoint.
          </p>

          <div className="checkpoint-grid">
            <div className="checkpoint-visual">
              <div className="checkpoint-diagram">
                <div className="checkpoint-center"></div>
                <div className="checkpoint-connections">
                  <span className="checkpoint-connection">QR Code</span>
                  <span className="checkpoint-connection">NFC Tag</span>
                  <span className="checkpoint-connection">Link</span>
                </div>
              </div>
              <div className="checkpoint-note">
                <div className="checkpoint-note-title">The key insight</div>
                <p className="checkpoint-note-text">
                  QR codes, NFC tags, and links are just ways people reach your Checkpoint. You control and record what happens when they arrive.
                </p>
              </div>
            </div>

            <div className="checkpoint-examples">
              <div className="checkpoint-example">
                <div className="checkpoint-example-icon"><HomeIcon size={20} /></div>
                <span className="checkpoint-example-text">An entrance, door, or gate</span>
              </div>
              <div className="checkpoint-example">
                <div className="checkpoint-example-icon"><LayoutGrid size={20} /></div>
                <span className="checkpoint-example-text">A room, hallway, or zone</span>
              </div>
              <div className="checkpoint-example">
                <div className="checkpoint-example-icon"><Monitor size={20} /></div>
                <span className="checkpoint-example-text">A poster, badge, or sticker</span>
              </div>
              <div className="checkpoint-example">
                <div className="checkpoint-example-icon"><Settings size={20} /></div>
                <span className="checkpoint-example-text">A help desk or terminal</span>
              </div>
              <div className="checkpoint-example">
                <div className="checkpoint-example-icon"><FileText size={20} /></div>
                <span className="checkpoint-example-text">A document, video, or instruction</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Section */}
      <section className="why-section" id="why">
        <div className="container">
          <div className="section-label">Why Confcall</div>
          <h2 className="section-title">Physical and digital spaces<br/>break without proper control</h2>

          <div className="why-grid">
            <div className="why-column why-problems">
              <h3 className="why-column-title problem-title">
                <XCircle size={16} />
                The Problem
              </h3>
              <div className="why-items">
                <div className="why-item">
                  <div className="why-icon problem-icon"><Clock size={16} /></div>
                  <div className="why-item-content">
                    <h4>Content becomes outdated</h4>
                    <p>Posters and links rot. Once printed or shared, you lose control.</p>
                  </div>
                </div>
                <div className="why-item">
                  <div className="why-icon problem-icon"><Lock size={16} /></div>
                  <div className="why-item-content">
                    <h4>No way to disable or change</h4>
                    <p>Interactions can't be disabled, redirected, or modified after deployment.</p>
                  </div>
                </div>
                <div className="why-item">
                  <div className="why-icon problem-icon"><AlertCircle size={16} /></div>
                  <div className="why-item-content">
                    <h4>No visibility</h4>
                    <p>Nobody knows what actually happened. Zero audit trail.</p>
                  </div>
                </div>
                <div className="why-item">
                  <div className="why-icon problem-icon"><WifiOff size={16} /></div>
                  <div className="why-item-content">
                    <h4>Offline fails</h4>
                    <p>Low-connectivity situations break the entire experience.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="why-column why-solutions">
              <h3 className="why-column-title solution-title">
                <CheckCircle2 size={16} />
                The Solution
              </h3>
              <div className="why-items">
                <div className="why-item">
                  <div className="why-icon solution-icon"><Star size={16} /></div>
                  <div className="why-item-content">
                    <h4>Control under pressure</h4>
                    <p>Change destinations, disable access, or redirect users in real-time.</p>
                  </div>
                </div>
                <div className="why-item">
                  <div className="why-icon solution-icon"><Activity size={16} /></div>
                  <div className="why-item-content">
                    <h4>Offline tolerance</h4>
                    <p>Works in low-connectivity environments. Never leaves users stranded.</p>
                  </div>
                </div>
                <div className="why-item">
                  <div className="why-icon solution-icon"><Shield size={16} /></div>
                  <div className="why-item-content">
                    <h4>Auditability and trust</h4>
                    <p>Full audit trail of every interaction. Know exactly what happened.</p>
                  </div>
                </div>
                <div className="why-item">
                  <div className="why-icon solution-icon"><RefreshCw size={16} /></div>
                  <div className="why-item-content">
                    <h4>Reversible operations</h4>
                    <p>Flexible and reversible. Undo mistakes, test changes, roll back.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section" id="features">
        <div className="container">
          <div className="section-label">Capabilities</div>
          <h2 className="section-title">Built for operations teams</h2>
          <p className="section-description">
            Everything you need to manage physical and digital touchpoints at scale.
          </p>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon"><QrCode size={24} /></div>
              <h3 className="feature-title">QR, NFC, Links</h3>
              <p className="feature-description">Generate and manage multiple access methods for every Checkpoint.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon"><Shield size={24} /></div>
              <h3 className="feature-title">Full Audit Log</h3>
              <p className="feature-description">Every scan, click, and interaction is recorded and searchable.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon"><Globe size={24} /></div>
              <h3 className="feature-title">Offline Ready</h3>
              <p className="feature-description">Works in basements, stadiums, and anywhere connectivity is spotty.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon"><RefreshCw size={24} /></div>
              <h3 className="feature-title">Instant Updates</h3>
              <p className="feature-description">Change destinations in real-time. No reprinting, no broken links.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-card">
            <h2 className="cta-title">Ready to take control?</h2>
            <p className="cta-description">Create your first Checkpoint in seconds. No credit card required.</p>
            <div className="cta-buttons">
              <button className="btn-primary-light" onClick={login}>Get Started Free</button>
              <a href="#checkpoints" className="btn-secondary-light">Learn More</a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="container">
          <div className="footer-content">
            <p className="footer-copy">© 2025 Confcall. All rights reserved.</p>
            <ul className="footer-links">
              <li><a href="#">Privacy</a></li>
              <li><a href="#">Terms</a></li>
              <li><a href="#">Documentation</a></li>
              <li><a href="#">Contact</a></li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
